import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    Card,
    CardBody,
    Badge,
    Table,
    Input,
    Spinner,
} from "reactstrap";
import api from "../../api";
import { useToast } from "../../context/ToastContext";

export interface RelatorioEncerramentoAlunoDTO {
    alunoId: number;
    referenceCode: number;
    nome: string;
    cpf: string;
    telefone: string;
    email?: string;
    cursoNome: string;
    temVenda: boolean;
    produtosContratados: string;
    valorTotalContratado: number;
    valorTotalPago: number;
    valorPendente: number;
    statusQuitacao: string;
    totalParcelas: number;
    parcelasPagas: number;
    parcelasPendentes: number;
}

export interface RelatorioEncerramentoContratoDTO {
    contratoId: number;
    description?: string;
    contratoDescricao?: string;
    turma: string;
    instituicaoNome: string;
    cidade: string;
    estado: string;
    anoConclusao?: number;
    completionDate?: string;
    dataFinalizacao?: string;
    totalFormandos?: number;
    totalAlunosTurma?: number;
    totalFormandosComVenda?: number;
    totalAlunosCompraramm?: number;
    totalGeralContratado?: number;
    valorTotalGeralContratado?: number;
    totalGeralPago?: number;
    valorTotalGeralRecebido?: number;
    totalGeralPendente?: number;
    valorTotalGeralPendente?: number;
    totalQuitados?: number;
    totalAlunosQuitados?: number;
    totalPendentes?: number;
    totalAlunosPendentes?: number;
    percentualInadimplencia?: number;
    totalDespesasGerais?: number;
    totalDespesasFinanceiras?: number;
    totalGeralDespesas?: number;
    formasPagamento?: {
        formaPagamento: string;
        quantidadeLancamentos: number;
        valorTotal: number;
        valorPago: number;
        valorPendente: number;
    }[];
    despesas?: {
        id: number;
        descricao: string;
        tipoCustoNome: string;
        isFinancial: boolean;
        valor: number;
        data?: string;
        recebedor?: string;
        observacao?: string;
    }[];
    alunos: RelatorioEncerramentoAlunoDTO[];
}

interface Props {
    isOpen: boolean;
    toggle: () => void;
    contratoId: number | null;
}

export const RelatorioEncerramentoModal: React.FC<Props> = ({ isOpen, toggle, contratoId }) => {
    const showToast = useToast();
    const [loading, setLoading] = useState<boolean>(false);
    const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
    const [relatorio, setRelatorio] = useState<RelatorioEncerramentoContratoDTO | null>(null);
    const [busca, setBusca] = useState<string>("");
    const [filtroStatus, setFiltroStatus] = useState<string>("todos");

    useEffect(() => {
        if (isOpen && contratoId) {
            setLoading(true);
            setBusca("");
            setFiltroStatus("todos");
            api
                .get<RelatorioEncerramentoContratoDTO>(`/Contrato/${contratoId}/relatorio-encerramento`)
                .then((res) => {
                    setRelatorio(res.data);
                })
                .catch((err) => {
                    console.error("Erro ao carregar relatório de encerramento:", err);
                    showToast("error", "Não foi possível carregar os dados de encerramento do contrato.");
                })
                .finally(() => setLoading(false));
        } else {
            setRelatorio(null);
        }
    }, [isOpen, contratoId]);

    const handleDownloadPdf = async () => {
        if (!contratoId) return;
        setDownloadingPdf(true);
        try {
            const response = await api.get(`/Contrato/${contratoId}/relatorio-encerramento/export/pdf`, {
                responseType: "blob",
            });
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `relatorio-encerramento-contrato-${contratoId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Erro ao baixar PDF:", err);
            showToast("error", "Erro ao exportar PDF de encerramento.");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const formatCurrency = (val?: number) => {
        return (val || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    };

    const alunosFiltrados = (relatorio?.alunos || []).filter((al) => {
        const matchText =
            al.nome?.toLowerCase().includes(busca.toLowerCase()) ||
            al.cpf?.includes(busca) ||
            al.referenceCode?.toString().includes(busca) ||
            al.cursoNome?.toLowerCase().includes(busca.toLowerCase());

        if (!matchText) return false;
        if (filtroStatus === "quitados") return al.statusQuitacao === "Quitado";
        if (filtroStatus === "pendentes") return al.valorPendente > 0;
        if (filtroStatus === "semVenda") return !al.temVenda;
        return true;
    });

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" className="modal-dialog-centered" style={{ maxWidth: "90vw" }}>
            <ModalHeader toggle={toggle} className="bg-primary text-white">
                <i className="fa-solid fa-flag-checkered mr-2"></i> Relatório de Encerramento de Contrato
                {relatorio && ` - Contrato #${relatorio.contratoId} (${relatorio.description})`}
            </ModalHeader>
            <ModalBody style={{ maxHeight: "78vh", overflowY: "auto" }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Carregando balanço financeiro dos formandos...</p>
                    </div>
                ) : !relatorio ? (
                    <div className="text-center py-5 text-muted">Nenhum dado encontrado para este contrato.</div>
                ) : (
                    <>
                        {/* HEADER CARDS */}
                        {(() => {
                            const totalContratado = relatorio.valorTotalGeralContratado ?? relatorio.totalGeralContratado ?? 0;
                            const totalRecebido = relatorio.valorTotalGeralRecebido ?? relatorio.totalGeralPago ?? 0;
                            const totalPendente = relatorio.valorTotalGeralPendente ?? relatorio.totalGeralPendente ?? 0;
                            const totalTurma = relatorio.totalAlunosTurma ?? relatorio.totalFormandos ?? relatorio.alunos?.length ?? 0;
                            const totalComVenda = relatorio.totalAlunosCompraramm ?? relatorio.totalFormandosComVenda ?? 0;
                            const totalQuit = relatorio.totalAlunosQuitados ?? relatorio.totalQuitados ?? 0;
                            const totalPend = relatorio.totalAlunosPendentes ?? relatorio.totalPendentes ?? 0;

                            return (
                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm bg-light">
                                            <CardBody className="p-3">
                                                <small className="text-muted d-block font-weight-bold">INSTITUIÇÃO / TURMA</small>
                                                <h6 className="font-weight-bold mb-1 text-dark text-truncate" title={relatorio.instituicaoNome}>
                                                    {relatorio.instituicaoNome || "-"}
                                                </h6>
                                                <small className="text-muted">
                                                    {relatorio.turma || "Geral"} • {relatorio.cidade}/{relatorio.estado}
                                                </small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm text-white" style={{ backgroundColor: "#2dce89" }}>
                                            <CardBody className="p-3">
                                                <small className="d-block text-white-50 font-weight-bold">TOTAL CONTRATADO</small>
                                                <h4 className="font-weight-bold mb-0 text-white">{formatCurrency(totalContratado)}</h4>
                                                <small className="text-white-50">{totalComVenda} com venda de {totalTurma} alunos</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm text-white" style={{ backgroundColor: "#11cdef" }}>
                                            <CardBody className="p-3">
                                                <small className="d-block text-white-50 font-weight-bold">TOTAL PAGO (RECEBIDO)</small>
                                                <h4 className="font-weight-bold mb-0 text-white">{formatCurrency(totalRecebido)}</h4>
                                                <small className="text-white-50">{totalQuit} alunos 100% quitados</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card className="border-0 shadow-sm text-white" style={{ backgroundColor: totalPendente > 0 ? "#f5365c" : "#2dce89" }}>
                                            <CardBody className="p-3">
                                                <small className="d-block text-white-50 font-weight-bold">VALOR TOTAL PENDENTE</small>
                                                <h4 className="font-weight-bold mb-0 text-white">{formatCurrency(totalPendente)}</h4>
                                                <small className="text-white-50">{totalPend} formandos com débito pendente</small>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            );
                        })()}

                        {/* ESPECIFICAÇÃO DE FORMAS DE PAGAMENTO E DESPESAS */}
                        <Row className="mb-3">
                            {relatorio.formasPagamento && relatorio.formasPagamento.length > 0 && (
                                <Col md={6}>
                                    <Card className="border shadow-sm mb-3">
                                        <CardBody className="p-3">
                                            <h6 className="font-weight-bold text-dark mb-2">
                                                <i className="fa-solid fa-money-bill-wave text-success mr-2"></i>
                                                Recebimentos por Forma de Pagamento
                                            </h6>
                                            <Table responsive size="sm" className="table-sm mb-0" style={{ fontSize: "11px" }}>
                                                <thead>
                                                    <tr className="text-muted">
                                                        <th>Forma</th>
                                                        <th className="text-center">Qtd</th>
                                                        <th className="text-right">Previsto</th>
                                                        <th className="text-right">Pago</th>
                                                        <th className="text-right">Pendente</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {relatorio.formasPagamento.map((fp, idx) => (
                                                        <tr key={idx}>
                                                            <td className="font-weight-bold">{fp.formaPagamento}</td>
                                                            <td className="text-center">{fp.quantidadeLancamentos}</td>
                                                            <td className="text-right">{formatCurrency(fp.valorTotal)}</td>
                                                            <td className="text-right text-success font-weight-bold">{formatCurrency(fp.valorPago)}</td>
                                                            <td className="text-right text-danger">{formatCurrency(fp.valorPendente)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}

                            {relatorio.despesas && (
                                <Col md={relatorio.formasPagamento && relatorio.formasPagamento.length > 0 ? 6 : 12}>
                                    <Card className="border shadow-sm mb-3">
                                        <CardBody className="p-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="font-weight-bold text-dark mb-0">
                                                    <i className="fa-solid fa-receipt text-danger mr-2"></i>
                                                    Despesas / Custos do Contrato
                                                </h6>
                                                <span className="badge badge-danger font-weight-bold">
                                                    Total: {formatCurrency(relatorio.totalGeralDespesas || 0)}
                                                </span>
                                            </div>
                                            {relatorio.despesas.length === 0 ? (
                                                <div className="text-muted small py-2">Nenhum custo lançado para este contrato.</div>
                                            ) : (
                                                <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                                                    <Table responsive size="sm" className="table-sm mb-0" style={{ fontSize: "11px" }}>
                                                        <thead>
                                                            <tr className="text-muted">
                                                                <th>Descrição</th>
                                                                <th>Data</th>
                                                                <th>Recebedor</th>
                                                                <th className="text-right">Valor</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {relatorio.despesas.map((d) => (
                                                                <tr key={d.id}>
                                                                    <td className="font-weight-bold text-truncate" style={{ maxWidth: "160px" }} title={d.descricao}>
                                                                        {d.descricao}
                                                                    </td>
                                                                    <td>{d.data || "-"}</td>
                                                                    <td className="text-truncate" style={{ maxWidth: "120px" }}>{d.recebedor || "-"}</td>
                                                                    <td className="text-right font-weight-bold text-danger">{formatCurrency(d.valor)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}
                        </Row>

                        {/* FILTERS */}
                        <Row className="mb-3 align-items-center">
                            <Col md={6}>
                                <Input
                                    type="text"
                                    placeholder="Buscar formando por nome, CPF, ref ou curso..."
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    bsSize="sm"
                                />
                            </Col>
                            <Col md={3}>
                                <Input
                                    type="select"
                                    value={filtroStatus}
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                    bsSize="sm"
                                >
                                    <option value="todos">Todos os Formandos ({relatorio.alunos.length})</option>
                                    <option value="pendentes">Apenas com Débito Pendente ({relatorio.totalPendentes})</option>
                                    <option value="quitados">Apenas Quitados ({relatorio.totalQuitados})</option>
                                    <option value="semVenda">Sem Venda Realizada</option>
                                </Input>
                            </Col>
                            <Col md={3} className="text-right">
                                <span className="small text-muted font-weight-bold">
                                    Exibindo {alunosFiltrados.length} de {relatorio.alunos.length} formandos
                                </span>
                            </Col>
                        </Row>

                        {/* TABLE */}
                        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                            <Table hover responsive size="sm" className="custom-table table-bordered mb-0" style={{ fontSize: "12px" }}>
                                <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th style={{ width: "60px" }}>Ref</th>
                                        <th>Formando</th>
                                        <th>Curso</th>
                                        <th>Telefone</th>
                                        <th>Produtos</th>
                                        <th className="text-right" style={{ width: "110px" }}>Contratado</th>
                                        <th className="text-right" style={{ width: "110px" }}>Pago</th>
                                        <th className="text-right" style={{ width: "110px" }}>Pendente</th>
                                        <th className="text-center" style={{ width: "90px" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alunosFiltrados.length > 0 ? (
                                        alunosFiltrados.map((al) => (
                                            <tr key={al.alunoId}>
                                                <td className="font-weight-bold">{al.referenceCode > 0 ? al.referenceCode : al.alunoId}</td>
                                                <td>
                                                    <span className="font-weight-bold text-dark">{al.nome}</span>
                                                    {al.cpf && <span className="d-block text-muted small">CPF: {al.cpf}</span>}
                                                </td>
                                                <td>{al.cursoNome || "-"}</td>
                                                <td>{al.telefone || "-"}</td>
                                                <td><small className="text-muted">{al.produtosContratados || "-"}</small></td>
                                                <td className="text-right">{formatCurrency(al.valorTotalContratado)}</td>
                                                <td className="text-right text-success font-weight-bold">{formatCurrency(al.valorTotalPago)}</td>
                                                <td className={`text-right font-weight-bold ${al.valorPendente > 0 ? "text-danger" : "text-muted"}`} style={{ backgroundColor: al.valorPendente > 0 ? "rgba(245, 54, 92, 0.05)" : undefined }}>
                                                    {formatCurrency(al.valorPendente)}
                                                    {al.parcelasPendentes > 0 && (
                                                        <span className="d-block text-muted small" style={{ fontSize: "10px" }}>
                                                            {al.parcelasPendentes} parc. pend.
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Badge
                                                        color={
                                                            al.statusQuitacao === "Quitado"
                                                                ? "success"
                                                                : al.statusQuitacao === "Parcial"
                                                                ? "warning"
                                                                : al.statusQuitacao === "Sem Venda"
                                                                ? "secondary"
                                                                : "danger"
                                                        }
                                                        pill
                                                    >
                                                        {al.statusQuitacao}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4 text-muted">
                                                Nenhum formando encontrado para o filtro aplicado.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
                <div>
                    <Button
                        color="primary"
                        onClick={handleDownloadPdf}
                        disabled={loading || downloadingPdf || !relatorio}
                    >
                        {downloadingPdf ? (
                            <>
                                <Spinner size="sm" className="mr-1" /> Gerando PDF...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-file-pdf mr-1"></i> Exportar PDF Oficial
                            </>
                        )}
                    </Button>
                </div>
                <Button color="secondary" onClick={toggle}>
                    Fechar
                </Button>
            </ModalFooter>
        </Modal>
    );
};
